import WarehouseSelector, { useActiveWarehouse, WarehouseSelectionRequired } from 'src/components/atoms/WarehouseSelector';
import * as Yup from 'yup';
import { AxiosRequestConfig } from 'axios';
import PageHeader from 'src/components/organisms/PageHeader';
import Footer from 'src/components/molecules/footer/Footer';
import ContentCardsContainer, { CardType, ContentCard, CardTab, HeaderTabs } from 'src/components/organisms/ContentCard';
import { Formik, FormikHelpers } from 'formik';
import SaveIcon from '@mui/icons-material/Save';
import PrintIcon from '@mui/icons-material/Print';
import SyncIcon from '@mui/icons-material/Sync';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { FormContainerMolecule } from 'src/components/molecules/formik/FormContainerMolecule';
import { FormField } from 'src/components/molecules/formik/FormField';
import CustomButton from 'src/components/atoms/buttons/CustomButton';
import FormHelper from 'src/utils/formHelper';
import { CustomDataGrid } from 'src/components/organisms/CustomDataGrid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatchOrderOptions } from 'src/hooks/forms/useOptions';
import { Alert, Box, Dialog, DialogContent, DialogTitle, Grid, MenuItem, TextField, Typography } from '@mui/material';
import useSubmit from 'src/hooks/forms/useSubmit';
import { dispatchDispatchOrder } from 'src/utils/apiRequests';
import { applyDispatchOrderShipmentResult, getDispatchOrderShipmentRows, normalizeDispatchOrderShipments, useDispatchOrderRowsTable, useDispatchOrderShipmentsTable } from 'src/hooks/table/configs/useDispatchOrdersTable';

import { useCancelDispatchOrderByPickingList, useGetDispatchOrderByWarehouse, useGetDispatchOrderByPickingList, useGetShipmentLabelsByWarehouse, useUpdateShipmentDispatchOrder, useTriggerSyncShipmentDispatchOrder } from 'src/hooks/api/requestConfigs/warehouse';
import { DispatchPrintingSettings, PrintOutcome, printDispatchPdf, PickingListPrintJob, PickingListPrintResult, describePrintOutcome, getDispatchResultData, useDispatchPrintingSettings, useDispatchResultPrinting, UploadAddressLabelButton } from 'src/components/organisms/printing/PrintingManager';
import { Edit } from '@mui/icons-material';
import { useSettingsContext } from 'src/hooks/useSettings';

const scheme = Yup.object().shape({
    DispatchOrderId: Yup.number().integer().positive().required('Dispatch Order is required'),
    Warehouse: Yup.number().integer().positive().required('Warehouse is required'),
    // PickingListId: Yup.number().integer().positive().required('Picking List is required'),
    OrderStatus: Yup.number().oneOf([5], 'Order must be Verification In Progress').required('Order Status is required'),
});

const EditDispatchOrder = () => <WarehouseSelectionRequired><EditDispatchOrderContent /></WarehouseSelectionRequired>;

const EditDispatchOrderContent = () => {
    const { settings, setSetting } = useSettingsContext();
    const { warehouseId, selectWarehouse } = useActiveWarehouse();
    const packages = settings?.dispatchOrdersPackages ?? '';
    const printDeliveryNote = settings?.dispatchOrdersPrintDeliveryNote === true || settings?.dispatchOrdersPrintDeliveryNote === 'true';
    const changeWarehouse = (value: number | string) => selectWarehouse(value);
    const changePackages = (value: string) => setSetting('dispatchOrdersPackages', value === '' ? '' : Number(value));
    const changePrintDeliveryNote = (value: boolean) => setSetting('dispatchOrdersPrintDeliveryNote', value);
    const [editId] = FormHelper.getEditPageConfig("Dispatch Order");
    const buildRequest = useCallback((values: any) => buildDispatchOrderRequest(values, packages, printDeliveryNote), [packages, printDeliveryNote]);
    const [result, submit] = useSubmit(buildRequest);
    const [submitting, setSubmitting] = useState(false);
    const printingSettings = useDispatchPrintingSettings();
    const dispatchResult = getDispatchResultData(result);
    // Printing intentionally receives every completed dispatch result, including a late
    // result for an order that is no longer loaded in the form.
    const { printing, printErrors, printNotices } = useDispatchResultPrinting(dispatchResult, printingSettings);
    const appliedDispatchResults = useRef<WeakSet<object>>(new WeakSet());
    const [scannedDispatchOrder, setScannedDispatchOrder] = useState<any>(null);
    const [dispatchOrderLookup, setDispatchOrderLookup] = useState<{ id: number | string; key: number } | null>(null);
    const lookupSequence = useRef(0);
    const [loadedDispatchOrderId, setLoadedDispatchOrderId] = useState<number | string | null>(null);
    const [formResetKey, setFormResetKey] = useState(0);
    const [formOrderCleared, setFormOrderCleared] = useState(false);
    const effectiveEditId = formOrderCleared ? null : editId;
    const initialValues: any = useDispatchOrderForm(effectiveEditId, defaultInitialValues, scannedDispatchOrder, warehouseId);
    const activeDispatchOrderId = scannedDispatchOrder?.DispatchOrderId ?? effectiveEditId ?? initialValues?.DispatchOrderId ?? null;
    const packagesOrderIdRef = useRef<string | null>(null);

    useEffect(() => {
        const orderId = initialValues?.DispatchOrderId == null ? null : String(initialValues.DispatchOrderId);
        if (packagesOrderIdRef.current === orderId) return;
        packagesOrderIdRef.current = orderId;
        if (orderId != null) setSetting('dispatchOrdersPackages', 1);
    }, [initialValues?.DispatchOrderId, setSetting]);

    // Compare the station selection only with the mapped order's Warehouse field.
    const orderWarehouse = initialValues?.Warehouse;
    const warehouseMismatch = activeDispatchOrderId != null && orderWarehouse != null &&
        warehouseId != null && String(orderWarehouse) !== String(warehouseId);
    const warehouseBlocked = warehouseId == null || (activeDispatchOrderId != null &&
        (orderWarehouse == null || warehouseMismatch));
    const warehouseBlockedRef = useRef(warehouseBlocked);
    warehouseBlockedRef.current = warehouseBlocked;
    const [orderTypeOptions, orderStatusOptions, orderPriorityOptions, deliveryMethodOptions, blockReasonOptions, enterDispatchedRowQtyLimit] = useDispatchOrderOptions();
    const [orderRowColumns, orderRowTableSettings] = useDispatchOrderRowsTable(activeDispatchOrderId);
    const [dispatchOrderRows, setDispatchOrderRows] = useState<any[]>([]);
    const [currentTab, setCurrentTab] = useState<string>('OrderRows');
    const [shipmentLabelJob, setShipmentLabelJob] = useState<ShipmentLabelJob | null>(null);
    const shipmentLabelSequence = useRef(0);
    const [shipmentLabelNotice, setShipmentLabelNotice] = useState<{ severity: 'success' | 'warning' | 'error'; message: string } | null>(null);
    const finishShipmentLabelPrint = useCallback((result: ShipmentLabelPrintResult) => {
        setShipmentLabelJob(null);
        if (result.error) { setShipmentLabelNotice({ severity: 'error', message: result.error }); return; }
        if (!result.outcome) return;
        const { method, printer, reason } = result.outcome;
        setShipmentLabelNotice({ severity: method === 'printer' ? 'success' : 'warning', message: method === 'printer' ? `Address label sent to "${printer}".` : `${method === 'savePdf' ? 'Address label PDF download requested.' : 'Address label printing skipped.'}${reason ? ` ${reason}` : ''}` });
    }, []);
    const reprintShipmentLabel = useCallback((row: any) => {
        if (warehouseBlockedRef.current || warehouseId == null || activeDispatchOrderId == null || row.ShipmentId == null || !row.ShipmentPackageCount) return;
        if (row.ShipmentPackages_Id == null && row.ShipmentPackageCount !== 1) return;
        const job: ShipmentLabelJob = { key: ++shipmentLabelSequence.current, rowId: row.ShipmentPackageRowId, shipmentId: row.ShipmentId, packageId: row.ShipmentPackages_Id ?? null, packageCount: row.ShipmentPackageCount, warehouseId, orderId: activeDispatchOrderId, settings: printingSettings };
        setShipmentLabelNotice(null);
        setShipmentLabelJob((current) => current ?? job);
    }, [warehouseId, activeDispatchOrderId, printingSettings]);
    // Shipment tracking: submit the current table rows, including unchanged rows for resync.
    const trackingScope = `${activeDispatchOrderId ?? ''}:${warehouseId ?? ''}`;
    const trackingScopeRef = useRef(trackingScope);
    trackingScopeRef.current = trackingScope;
    const [shipmentRows, setShipmentRows] = useState<any[]>([]);
    const [trackingJob, setTrackingJob] = useState<UpdateShipmentJob | null>(null);
    const trackingSequence = useRef(0);
    const [trackingNotice, setTrackingNotice] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);
    const trackingDisabledReason = warehouseBlocked ? 'Select the warehouse for this dispatch order before syncing tracking.' : trackingJob ? 'Tracking is being saved and synced. Please wait.' : undefined;
    const [shipmentColumns, shipmentTableSettings] = useDispatchOrderShipmentsTable(initialValues?.Shipments, currentTab === 'Shipments', { onReprintLabel: reprintShipmentLabel, reprintingRowId: shipmentLabelJob?.rowId, disabled: warehouseBlocked, trackingRows: shipmentRows, onTrackingRowsChange: setShipmentRows, trackingDisabled: trackingJob != null, trackingScope });

    useEffect(() => {
        setShipmentRows(getDispatchOrderShipmentRows(initialValues?.Shipments));
        setTrackingNotice(null);
    }, [trackingScope, initialValues?.Shipments]);

    const syncTracking = () => {
        if (warehouseBlockedRef.current || trackingJob || warehouseId == null) return;
        const api = shipmentTableSettings.apiRef?.current;
        if (!api?.getRowModels) { setTrackingNotice({ severity: 'error', message: 'The shipments table is not ready. Please try again.' }); return; }
        const rows = Array.from(api.getRowModels().values()).map((row: any) => {
            let currentRow = row;
            // Read the edit buffer as well: clicking Sync must include the cell being edited.
            ['ShipmentPackages_TrackingNumber', 'ShipmentPackages_ReturnLabelTrackingNumber'].forEach((field) => {
                if (api.getCellMode(api.getRowId(row), field) === 'edit') currentRow = { ...currentRow, ...api.getRowWithUpdatedValues(api.getRowId(row), field) };
            });
            return { ...currentRow, ShipmentPackages_TrackingNumber: String(currentRow.ShipmentPackages_TrackingNumber ?? ''), ShipmentPackages_ReturnLabelTrackingNumber: String(currentRow.ShipmentPackages_ReturnLabelTrackingNumber ?? '') };
        });
        if (!rows.length) { setTrackingNotice({ severity: 'error', message: 'There are no shipment packages to sync.' }); return; }
        if (rows.some((row) => row.ShipmentId == null || row.ShipmentPackages_Id == null)) { setTrackingNotice({ severity: 'error', message: 'Reload the order to get shipment and package IDs before syncing tracking.' }); return; }
        rows.forEach((row) => ['ShipmentPackages_TrackingNumber', 'ShipmentPackages_ReturnLabelTrackingNumber'].forEach((field) => {
            if (api.getCellMode(api.getRowId(row), field) === 'edit') api.stopCellEditMode({ id: api.getRowId(row), field });
        }));
        setShipmentRows(rows);
        setTrackingNotice(null);
        const job: UpdateShipmentJob = { key: ++trackingSequence.current, scope: trackingScope, warehouseId, rows };
        setTrackingJob((current) => current ?? job);
    };
    const finishTracking = (job: UpdateShipmentJob, completedRows: any[], error?: string) => {
        if (trackingScopeRef.current === job.scope && (error || completedRows.length === job.rows.length)) setTrackingNotice({ severity: error ? 'error' : 'success', message: error ?? 'Tracking saved and customer sync triggered.' });
        const remainingRows = job.rows.filter((row) => !completedRows.includes(row));
        setTrackingJob(!error && remainingRows.length ? { ...job, rows: remainingRows } : null);
    };

    useEffect(() => {
        if (shipmentLabelJob && (warehouseBlocked || String(shipmentLabelJob.orderId) !== String(activeDispatchOrderId) || String(shipmentLabelJob.warehouseId) !== String(warehouseId))) setShipmentLabelJob(null);
    }, [shipmentLabelJob, warehouseBlocked, activeDispatchOrderId, warehouseId]);
    const [pickingListJob, setPickingListJob] = useState<{ key: number; orderId: number | string; warehouseId: number | string; settings: typeof printingSettings } | null>(null);
    const printSequence = useRef(0);
    const [pickingListNotice, setPickingListNotice] = useState<PickingListPrintResult | null>(null);
    const [cancelDispatchOrderJob, setCancelDispatchOrderJob] = useState<{ key: number; pickingListId: number | string; warehouseId: number | string } | null>(null);
    const cancelSequence = useRef(0);
    const formikRef = useRef<any>(null);

    const clearDispatchOrderForm = useCallback(() => {
        const url = new URL(window.location.href);
        url.searchParams.delete('id');
        window.history.replaceState({}, '', url.toString());

        setFormOrderCleared(true);
        setScannedDispatchOrder(null);
        setDispatchOrderLookup(null);
        setLoadedDispatchOrderId(null);
        setDispatchOrderRows([]);
        setCurrentTab('OrderRows');
        setPickingListNotice(null);
        setPickingListJob(null);
        setShipmentLabelJob(null);
        setShipmentLabelNotice(null);
        setSubmitting(false);
        formikRef.current?.resetForm({ values: defaultInitialValues });
        setFormResetKey((current) => current + 1);
    }, []);

    const handleCancelDispatchOrder = useCallback((orderStatus: any, pickingListId: number | string | null, orderWarehouseId: number | string | null) => {
        if (Number(orderStatus?.Key ?? orderStatus) === 5 && pickingListId != null && orderWarehouseId != null) {
            setCancelDispatchOrderJob({ key: ++cancelSequence.current, pickingListId, warehouseId: orderWarehouseId });
        }
        clearDispatchOrderForm();
    }, [clearDispatchOrderForm]);

    const finishPickingListPrint = useCallback((result: PickingListPrintResult) => {
        setPickingListNotice(result);
        setPickingListJob(null);
    }, []);

    const handleDispatchOrderScan = useCallback((value: number | string) => {
        if (warehouseBlockedRef.current) return;
        lookupSequence.current += 1;
        setDispatchOrderLookup({ id: value, key: lookupSequence.current });
    }, []);

    const handleDispatchOrderLookupResolved = useCallback((dispatchOrder: any | null) => {
        setDispatchOrderLookup(null);

        // No dispatch order found: leave the current page exactly as it is.
        if (!dispatchOrder?.DispatchOrderId) return;

        const currentDispatchOrderId = scannedDispatchOrder?.DispatchOrderId ?? effectiveEditId;

        // Scanning the order already on screen should not reset local changes.
        if (String(dispatchOrder.DispatchOrderId) === String(currentDispatchOrderId)) return;

        const url = new URL(window.location.href);
        url.searchParams.set('id', String(dispatchOrder.DispatchOrderId));
        window.history.replaceState({}, '', url.toString());

        setFormOrderCleared(false);
        setScannedDispatchOrder(dispatchOrder);
        setLoadedDispatchOrderId(dispatchOrder.DispatchOrderId);
    }, [effectiveEditId, scannedDispatchOrder]);

    useEffect(() => {
        if (loadedDispatchOrderId == null) return;

        const timeoutId = window.setTimeout(() => {
            setLoadedDispatchOrderId(null);
        }, 1500);

        return () => window.clearTimeout(timeoutId);
    }, [loadedDispatchOrderId]);

    useEffect(() => {
        if (!Array.isArray(initialValues?.DispatchOrderRows)) return;

        setDispatchOrderRows(initialValues.DispatchOrderRows);
    }, [initialValues?.DispatchOrderId, initialValues?.DispatchOrderRows]);

    useEffect(() => {
        orderRowTableSettings?.apiRef?.current?.setRows?.(dispatchOrderRows);
    }, [dispatchOrderRows, orderRowTableSettings?.apiRef, currentTab]);

    const tabs: CardTab[] = [
        ...(activeDispatchOrderId ? [{ value: 'OrderRows', label: 'Order Rows' }] : []),
        { value: 'Receiver', label: 'Receiver' },
        ...(activeDispatchOrderId ? [{ value: 'Shipments', label: 'Shipments' }] : []),

    ];

    useEffect(() => {
        if (!result) return;
        setSubmitting(false);
    }, [result]);

    useEffect(() => {
        if (!dispatchResult || appliedDispatchResults.current.has(dispatchResult)) return;
        appliedDispatchResults.current.add(dispatchResult);

        const returnedOrder = dispatchResult.DispatchOrder;
        // A late response must not replace a different order the operator scanned.
        if (String(returnedOrder.DispatchOrderId) !== String(activeDispatchOrderId)) return;

        setScannedDispatchOrder(applyDispatchOrderShipmentResult(dispatchResult, initialValues?.Shipments));
        setDispatchOrderRows(returnedOrder.DispatchOrderRows ?? []);
    }, [dispatchResult, activeDispatchOrderId, initialValues?.Shipments]);

    return (
        <>
            {trackingJob && <UpdateShipment key={`${trackingJob.key}:${trackingJob.rows[0].ShipmentId}`} job={trackingJob} onComplete={finishTracking} />}
            {shipmentLabelJob && !warehouseBlocked && String(shipmentLabelJob.orderId) === String(activeDispatchOrderId) && String(shipmentLabelJob.warehouseId) === String(warehouseId) && <ShipmentLabelReprintJob key={shipmentLabelJob.key} job={shipmentLabelJob} onComplete={finishShipmentLabelPrint} />}
            {pickingListJob && <PickingListPrintJob key={pickingListJob.key} orderId={pickingListJob.orderId} warehouseId={pickingListJob.warehouseId} settings={pickingListJob.settings} onComplete={finishPickingListPrint} />}
            {cancelDispatchOrderJob && <CancelDispatchOrderVerification key={cancelDispatchOrderJob.key} pickingListId={cancelDispatchOrderJob.pickingListId} warehouseId={cancelDispatchOrderJob.warehouseId} />}
            {dispatchOrderLookup && warehouseId != null && (
                <DispatchOrderLookup key={`${dispatchOrderLookup.key}:${warehouseId}`} lookupId={dispatchOrderLookup.id} warehouseId={warehouseId} onResolved={handleDispatchOrderLookupResolved} />
            )}

            <Formik key={`${activeDispatchOrderId ?? 'new-dispatch-order'}:${formResetKey}`} innerRef={formikRef} initialValues={initialValues} validateOnBlur={true} validateOnChange={false} validationSchema={scheme} onSubmit={(values: any, formikHelpers: FormikHelpers<any>) => {
                if (warehouseBlockedRef.current || String(values.Warehouse) !== String(warehouseId) ||
                    !isReadyToDispatch(values.OrderStatus, dispatchOrderRows)) {
                    formikHelpers.setSubmitting(false);
                    return;
                }

                setSubmitting(true);
                return submit(values, formikHelpers);
            }} enableReinitialize>
                {(props): JSX.Element => {
                    const readyToDispatch = !warehouseBlocked && isReadyToDispatch(props.values.OrderStatus, dispatchOrderRows);
                    const receiverSummary = [
                        props.values.Company || props.values.Name,
                        props.values.StreetAddress,
                        props.values.Country,
                    ].filter(Boolean).join(', ');

                    const displayedDispatchOrderId = loadedDispatchOrderId ?? props.values?.DispatchOrderId ?? activeDispatchOrderId;
                    const dispatchOrderTitle = displayedDispatchOrderId != null
                        ? `Dispatch Station - ${displayedDispatchOrderId}`
                        : 'Dispatch Station';
                    const dispatchSettingsFormikProps = {
                        ...props,
                        values: { ...props.values, Packages: packages, PrintDeliveryNote: printDeliveryNote },
                        handleChange: (event: any) => {
                            if (event?.target?.name === 'Packages') { changePackages(event.target.value); return; }
                            if (event?.target?.name === 'PrintDeliveryNote') { changePrintDeliveryNote(Boolean(event.target.checked)); return; }
                            props.handleChange(event);
                        },
                        setFieldValue: (field: string, value: any, shouldValidate?: boolean) => {
                            if (field === 'Packages') { changePackages(value); return Promise.resolve(); }
                            if (field === 'PrintDeliveryNote') { changePrintDeliveryNote(Boolean(value)); return Promise.resolve(); }
                            return props.setFieldValue(field, value, shouldValidate);
                        },
                    };

                    return (
                        <div>
                            <PageHeader {...{
                                title: dispatchOrderTitle,
                                titleFlashKey: loadedDispatchOrderId,
                                initialValues: initialValues,
                                subTitle: "Dispatch Orders from warehouse",
                                components: [
                                    { component: <WarehouseSelector value={warehouseId} onChange={changeWarehouse} /> },
                                    {
                                        position: "center",
                                        component: <DispatchOrderHeaderBox key={`${activeDispatchOrderId ?? 'new-dispatch-order'}:${warehouseId}`} dispatchOrderId={activeDispatchOrderId} pickingListId={props.values.PickingListId} onVerificationStarted={() => props.setFieldValue('OrderStatus', 5, false)} warehouseId={initialValues.Warehouse} disabled={warehouseBlocked} orderStatus={props.values.OrderStatus} orderStatusOptions={orderStatusOptions} readyToDispatch={readyToDispatch} dispatchOrderRows={dispatchOrderRows} setDispatchOrderRows={setDispatchOrderRows} enterDispatchedRowQtyLimit={enterDispatchedRowQtyLimit} onDispatchOrderScan={handleDispatchOrderScan} onDispatch={() => { if (readyToDispatch && !submitting) props.handleSubmit(); }} onCancel={() => handleCancelDispatchOrder(props.values.OrderStatus, props.values.PickingListId, props.values.Warehouse)} />
                                    },
                                    // {
                                    //     component: <CustomButton isSubmitting={props.isSubmitting} onClick={() => { props.handleSubmit(); }} title="Save" icon={<SaveIcon />} />
                                    // },
                                    {
                                        component: <CustomButton disabled={!readyToDispatch || submitting} isSubmitting={submitting} onClick={() => { props.handleSubmit(); }} title="Dispatch" icon={<ArrowForwardIcon />} />
                                    },
                                    ...(activeDispatchOrderId ? [{
                                        component: <CustomButton disabled={submitting} onClick={() => handleCancelDispatchOrder(props.values.OrderStatus, props.values.PickingListId, props.values.Warehouse)} title="Cancel" variant="outlined" />
                                    }] : []),

                                    ...(activeDispatchOrderId ? [{
                                        component: <CustomButton disabled={warehouseBlocked || pickingListJob != null} onClick={() => {
                                            if (warehouseBlockedRef.current) return;
                                            setPickingListNotice(null);
                                            setPickingListJob({ key: ++printSequence.current, orderId: activeDispatchOrderId,
                                                warehouseId: initialValues.Warehouse, settings: printingSettings });
                                        }} title={props.values.PickingListId ? "Reprint Picking List" : "Print Picking List"} icon={<PrintIcon />} />
                                    }] : []),
                                ],
                                alertData: { dataArray: (result ?? false) ? [result] : null }
                            }} />

                            {/* {trackingNotice && <Alert severity={trackingNotice.severity} onClose={() => setTrackingNotice(null)} sx={{ mb: 2 }}>{trackingNotice.message}</Alert>}
                            {shipmentLabelNotice && <Alert severity={shipmentLabelNotice.severity} onClose={() => setShipmentLabelNotice(null)} sx={{ mb: 2 }}>{shipmentLabelNotice.message}</Alert>} */}

                            <Dialog open={warehouseMismatch} fullWidth maxWidth="md" disableEscapeKeyDown aria-labelledby="wrong-warehouse-title" aria-describedby="wrong-warehouse-description">
                                <DialogTitle id="wrong-warehouse-title" sx={{ color: 'error.main', fontSize: '2rem', fontWeight: 700 }}>Wrong warehouse</DialogTitle>
                                <DialogContent sx={{ pb: 4 }}>
                                    <Typography id="wrong-warehouse-description" variant="h6" sx={{ mb: 3 }}>
                                        This dispatch order belongs to warehouse {String(orderWarehouse)}, but your selected
                                        warehouse is {String(warehouseId)}. Dispatching, printing and scanning are blocked.
                                        If you want to work on this order, you need to switch warehouse first.
                                    </Typography>
                                    <WarehouseSelector value={warehouseId} onChange={changeWarehouse} />
                                </DialogContent>
                            </Dialog>

                            <Box component="fieldset" disabled={warehouseBlocked} sx={{ border: 0, m: 0, p: 0, minWidth: 0, pointerEvents: warehouseBlocked ? 'none' : 'auto' }}>
                                <ContentCardsContainer>

                                    <ContentCard {...{ cardId: "Order", md: 6 }}>
                                        <FormContainerMolecule formikProps={props}>
                                            <FormField fieldProps={{ name: "DispatchOrderType", select: true, label: "Order Type", md: 5.8 }} formikProps={props}>
                                                {orderTypeOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                                            </FormField>

                                            <FormField fieldProps={{ name: "OrderStatus", select: true, label: "Order Status", md: 5.8 }} formikProps={props}>
                                                {orderStatusOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                                            </FormField>

                                            <FormField fieldProps={{ name: "Priority", select: true, label: "Priority", md: 5.8 }} formikProps={props}>
                                                {orderPriorityOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                                            </FormField>

                                            <FormField fieldProps={{ name: "DeliveryMethod", select: true, label: "Delivery Method", md: 5.8 }} formikProps={props}>
                                                {deliveryMethodOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                                            </FormField>

                                            <FormField fieldProps={{ name: "DispatchOrderBlockId", select: true, label: "Block Reason", md: 5.8 }} formikProps={props}>
                                                {blockReasonOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                                            </FormField>

                                            <FormField fieldProps={{ name: "DispatchDate", date: true, label: "Dispatch Date", md: 5.8 }} formikProps={props}></FormField>

                                            <Grid item xs={12} md={12} mt={2}>
                                                <TextField label="Customer / Address" value={receiverSummary} fullWidth disabled placeholder="Customer name, street, country" />
                                            </Grid>
                                        </FormContainerMolecule>
                                    </ContentCard>

                                    <ContentCard {...{ cardId: "Info", md: 6 }}>
                                        <FormContainerMolecule disabled={true} formikProps={props}>
                                            <FormField fieldProps={{ name: "DispatchOrderId", label: "Dispatch Order Id", md: 5.8 }} formikProps={props}></FormField>
                                            <FormField fieldProps={{ name: "Warehouse_Value", label: "Warehouse", md: 5.8 }} formikProps={props}></FormField>
                                            <FormField fieldProps={{ name: "ExternalOrderId", label: "External Order Id", md: 5.8 }} formikProps={props}></FormField>
                                            <FormField fieldProps={{ name: "WebOrderId", label: "Web Order Id", md: 5.8 }} formikProps={props}></FormField>
                                            <FormField fieldProps={{ name: "PickingListId", label: "Picking List Id", md: 5.8 }} formikProps={props}></FormField>
                                            <FormField fieldProps={{ name: "UnifaunPreparedId", label: "Unifaun Prepared Id", md: 5.8 }} formikProps={props}></FormField>
                                            <FormField fieldProps={{ name: "CreateDate", label: "Create Date", md: 5.8 }} formikProps={props}></FormField>
                                            <FormField fieldProps={{ name: "ExternalWarehouseStatus", label: "Warehouse Status", md: 5.8 }} formikProps={props}></FormField>
                                        </FormContainerMolecule>
                                        <FormContainerMolecule formikProps={dispatchSettingsFormikProps}>
                                            <FormField fieldProps={{ name: "Packages", type: "number", label: "Packages", md: 5.8, inputProps: { min: 1, step: 1 } }} formikProps={dispatchSettingsFormikProps}></FormField>
                                            <FormField fieldProps={{ name: "PrintDeliveryNote", checkbox: true, label: "Print Delivery Note", md: 5.8 }} formikProps={dispatchSettingsFormikProps}></FormField>
                                        </FormContainerMolecule>
                                    </ContentCard>

                                    <ContentCard {...{
                                        cardId: "TabContainer",
                                        contentPadding: 0,
                                        md: 12,
                                        cardTypes: [CardType.tabContainer],
                                        cardHeader: <HeaderTabs {...{ tabs, currentTab, setCurrentTab }} />,
                                        components: currentTab === 'Shipments' ? [{ component: <CustomButton title="Sync Tracking to Customer" icon={<SyncIcon />} disabled={Boolean(trackingDisabledReason)} tooltipText={trackingDisabledReason} isSubmitting={trackingJob != null} onClick={syncTracking} /> }, { component: <UploadAddressLabelButton settings={printingSettings} disabled={warehouseBlocked} /> }] : [],
                                    }}>
                                        {currentTab === "Receiver" && (
                                            <ContentCard cardId="Receiver" cardTypes={[CardType.tab]} contentPadding={2}>
                                                <FormContainerMolecule formikProps={props}>
                                                    <FormField fieldProps={{ name: "Company", label: "Company", md: 5.8 }} formikProps={props} />
                                                    <FormField fieldProps={{ name: "Name", label: "Name", md: 5.8 }} formikProps={props} />

                                                    <FormField fieldProps={{ name: "Department", label: "Department", md: 5.8 }} formikProps={props} />
                                                    <FormField fieldProps={{ name: "Country", label: "Country", md: 5.8 }} formikProps={props} />

                                                    <FormField fieldProps={{ name: "StreetAddress", label: "Street Address", md: 7.8 }} formikProps={props} />
                                                    <FormField fieldProps={{ name: "ZipCode", label: "Zip Code", md: 3.8 }} formikProps={props} />

                                                    <FormField fieldProps={{ name: "City", label: "City", md: 3.8 }} formikProps={props} />
                                                    <FormField fieldProps={{ name: "State", label: "State", md: 3.8 }} formikProps={props} />
                                                    <FormField fieldProps={{ name: "IsoCountryCode", label: "Country Code", md: 3.8 }} formikProps={props} />

                                                    <FormField fieldProps={{ name: "Phone", label: "Phone", md: 3.8 }} formikProps={props} />
                                                    <FormField fieldProps={{ name: "Cellphone", label: "Cellphone", md: 3.8 }} formikProps={props} />
                                                    <FormField fieldProps={{ name: "Email", label: "Email", md: 3.8 }} formikProps={props} />
                                                </FormContainerMolecule>
                                            </ContentCard>
                                        )}

                                        {activeDispatchOrderId && currentTab === "Shipments" && (
                                            <ContentCard cardId="Shipments" cardTypes={[CardType.tab]} contentPadding={0}>
                                                <CustomDataGrid columns={shipmentColumns} tableSettings={shipmentTableSettings} />
                                            </ContentCard>
                                        )}

                                        {activeDispatchOrderId && currentTab === "OrderRows" && (
                                            <ContentCard cardId="Order Rows" cardTypes={[CardType.tab]} contentPadding={0}>
                                                <CustomDataGrid columns={orderRowColumns} tableSettings={orderRowTableSettings} />
                                            </ContentCard>
                                        )}
                                    </ContentCard>

                                </ContentCardsContainer>
                            </Box>
                        </div>
                    );
                }}
            </Formik>

            <Footer />
        </>
    );
}

export default EditDispatchOrder;

const isReadyToDispatch = (orderStatus: any, rows: any[]): boolean =>
    Number(orderStatus) === 5 && rows?.length > 0 &&
    rows.every((row) => Number(row.DispatchedQty ?? 0) >= Number(row.OrderedQty ?? 0));

    const toDispatchOrderPayload = (values: any, packages: number | string, printDeliveryNote: boolean) => ({
        DispatchOrderId: values.DispatchOrderId,
        DeliveryAddress: null,
        DeliveryMethod: null,
        PrintDeliveryNote: printDeliveryNote,
        Packages: Number(packages) > 1 ? Array.from({ length: Number(packages) }, () => ({
            Id: 0,
            ShipmentId: 0,
            Weight: null,
            Length: null,
            Width: null,
            Height: null,
            Volume: null,
            DimensionUnitId: null,
            WeightUnitId: null,
            VolumeUnitId: null,
            TrackingNumber: null,
            ReturnLabelTrackingNumber: null,
        })) : null,
        WarehouseId: values.Warehouse,
    });

const buildDispatchOrderRequest = (values: any, packages: number | string, printDeliveryNote: boolean): AxiosRequestConfig => {
    const payload = toDispatchOrderPayload(values, packages, printDeliveryNote);
    const request = dispatchDispatchOrder(
        { ...payload, warehouseId: payload.WarehouseId },
        'POST',
        values.PickingListId,
    );

    // The helper reads lowercase warehouseId for the URL; the body uses only
    // the API's dispatch payload, without routing fields or product rows.
    console.log(request, payload);
    return { ...request, data: payload };
};

const defaultInitialValues = {
    Warehouse: null,
    DispatchOrderBlockId: null,
    ExternalOrderId: null,
    WebOrderId: null,
    DispatchDate: null,

};

const useDispatchOrderForm = (editId, defaultInitialValues, dispatchOrderOverride = null, warehouseId: number | string | null = null) => {
    const { rows: item }: any = useGetDispatchOrderByWarehouse(dispatchOrderOverride || warehouseId == null ? null : editId, warehouseId);

    const initialValues = useMemo(() => {
        const dispatchOrder = dispatchOrderOverride ?? (editId != null ? item : null);

        // A scanned dispatch order must take precedence even when this page was
        // originally opened without an ?id= query parameter.
        if (dispatchOrder) {
            const newInitialValues: any = FormHelper.mapToInitialValues(dispatchOrder);
            newInitialValues.Shipments = normalizeDispatchOrderShipments(dispatchOrder.Shipments);
            newInitialValues.isEditPage = dispatchOrder.DispatchOrderId ?? editId ?? "";
            return newInitialValues;
        }

        return editId != null
            ? { isEditPage: editId }
            : defaultInitialValues;
    }, [item, editId, dispatchOrderOverride, defaultInitialValues]);

    return initialValues;
};

const DispatchOrderHeaderBox = ({ disabled, dispatchOrderId, pickingListId, onVerificationStarted, warehouseId, orderStatus, orderStatusOptions, readyToDispatch, dispatchOrderRows, setDispatchOrderRows, enterDispatchedRowQtyLimit, onDispatchOrderScan, onDispatch, onCancel }) => {
    const [scannerValue, setScannerValue] = useState<string>('');
    const [verificationScan, setVerificationScan] = useState<{ productId: string; key: number } | null>(null);
    const verificationSequence = useRef(0);
    const verificationPending = useRef(false);
    const [scanError, setScanError] = useState('');
    const pendingWithoutPickingList = Number(orderStatus) === 4 && !pickingListId;
    const scanningBlocked = disabled || verificationScan != null;

    const [quantityRow, setQuantityRow] = useState<any>(null);
    const [manualQuantity, setManualQuantity] = useState<string>('');
    const [quantityError, setQuantityError] = useState<string>('');
    const scannerInputRef = useRef<HTMLInputElement>(null);
    const quantityInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (scanningBlocked) return;
        if (quantityRow) {
            quantityInputRef.current?.focus();
            quantityInputRef.current?.select();
        } else {
            scannerInputRef.current?.focus();
        }
    }, [quantityRow, scanningBlocked]);

    // Discard a pending entry if the order's rows have been replaced.
    useEffect(() => {
        if (quantityRow && !dispatchOrderRows?.includes(quantityRow)) {
            setQuantityRow(null);
            setManualQuantity('');
            setQuantityError('');
        }
    }, [dispatchOrderRows, quantityRow]);

    const closeQuantityEntry = () => {
        setQuantityRow(null);
        setManualQuantity('');
        setQuantityError('');
    };

    const handleQuantitySubmit = () => {
        if (disabled || verificationPending.current) return;
        if (pendingWithoutPickingList) {
            setScanError('Print the picking list first before updating product quantities.');
            closeQuantityEntry();
            return;
        }
        if (!quantityRow || !dispatchOrderRows?.includes(quantityRow)) {
            closeQuantityEntry();
            return;
        }

        const quantity = Number(manualQuantity);
        const orderedQuantity = Number(quantityRow.OrderedQty ?? 0);
        if (!manualQuantity.trim() || !Number.isFinite(quantity) || quantity <= 0 || quantity > orderedQuantity) {
            setQuantityError(`Enter a quantity greater than 0 and no more than ${orderedQuantity}.`);
            quantityInputRef.current?.focus();
            return;
        }

        // The entered value is the row's dispatched total, not an increment.
        setDispatchOrderRows((currentRows) => currentRows.map((row) =>
            row === quantityRow ? { ...row, DispatchedQty: quantity } : row
        ));
        closeQuantityEntry();
    };

    const dispatchOrderStatus = readyToDispatch
        ? 'Ready to dispatch'
        : orderStatusOptions?.find((option) => option.value === orderStatus)?.label ?? orderStatus ?? '-';

    const applyProductScan = (submittedValue: string) => {
        const targetRow = dispatchOrderRows.find((row) =>
            (String(row.ProductId) === submittedValue || (row.Barcode != null && String(row.Barcode) === submittedValue)) &&
            Number(row.DispatchedQty ?? 0) < Number(row.OrderedQty ?? 0)
        );
        if (!targetRow) return;

        const quantityLimit = Number(enterDispatchedRowQtyLimit);
        if (enterDispatchedRowQtyLimit != null && enterDispatchedRowQtyLimit !== '' &&
            Number.isFinite(quantityLimit) && quantityLimit >= 0 &&
            Number(targetRow.OrderedQty ?? 0) > quantityLimit) {
            setQuantityRow(targetRow);
            setManualQuantity('');
            setQuantityError('');
            return;
        }

        setDispatchOrderRows((currentRows) => currentRows.map((row) =>
            row === targetRow
                ? { ...row, DispatchedQty: Math.min(Number(row.OrderedQty ?? 0), Number(row.DispatchedQty ?? 0) + 1) }
                : row
        ));
    };

    const handleScannerSubmit = () => {
        if (disabled || verificationPending.current) return;
        if (quantityRow) {
            handleQuantitySubmit();
            return;
        }

        const submittedValue = scannerValue.trim();
        if (!submittedValue) return;
        setScannerValue('');
        setScanError('');

        const scannerCommand = submittedValue.toLowerCase();
        if (scannerCommand.includes('cancel')) {
            closeQuantityEntry();
            setVerificationScan(null);
            verificationPending.current = false;
            onCancel();
            return;
        }
        if (scannerCommand.includes('ok')) {
            if (readyToDispatch) onDispatch();
            return;
        }

        const productExists = dispatchOrderRows?.some((row) => (String(row.ProductId) === submittedValue || (row.Barcode != null && String(row.Barcode) === submittedValue)));

        if (productExists) {
            if (pendingWithoutPickingList) {
                setScanError('Print the picking list first before scanning products or updating quantities.');
                return;
            }
            if (Number(orderStatus) === 4) {
                verificationPending.current = true;
                setVerificationScan({ productId: submittedValue, key: ++verificationSequence.current });
                return;
            }
            applyProductScan(submittedValue);
            return;
        }

        onDispatchOrderScan(submittedValue);
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            {verificationScan && <StartDispatchOrderVerification key={verificationScan.key} pickingListId={pickingListId} warehouseId={warehouseId} dispatchOrderId={dispatchOrderId} onResolved={(success: boolean) => {
                verificationPending.current = false;
                setVerificationScan(null);
                if (disabled) return;
                if (!success) {
                    setScanError('Could not start verification. Scan the product again to retry.');
                    return;
                }
                onVerificationStarted();
                applyProductScan(verificationScan.productId);
            }} />}
            {scanError && <Alert severity="warning" sx={{ width: '100%' }}>{scanError}</Alert>}
            {verificationScan && <Typography variant="caption">Starting verification…</Typography>}
            <TextField inputRef={scannerInputRef} disabled={scanningBlocked || quantityRow != null} value={scannerValue} onChange={(event) => { setScannerValue(event.target.value); }} onKeyDown={(event) => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    handleScannerSubmit();
                }
            }} placeholder="Scan Dispatch Order, Picking List Or Product" autoFocus sx={{ width: { xs: 150, sm: 240, md: 360 } }} />

            {quantityRow && (
                <TextField disabled={scanningBlocked || pendingWithoutPickingList} inputRef={quantityInputRef} label="Dispatched quantity" type="number" value={manualQuantity} onChange={(event) => {
                    setManualQuantity(event.target.value);
                    setQuantityError('');
                }} onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        event.stopPropagation();
                        handleQuantitySubmit();
                    } else if (event.key === 'Escape') {
                        event.preventDefault();
                        event.stopPropagation();
                        closeQuantityEntry();
                    }
                }} inputProps={{ min: 0, max: Number(quantityRow.OrderedQty ?? 0), step: 'any' }} error={Boolean(quantityError)} helperText={quantityError || `Total for product ${quantityRow.ProductId} (max ${quantityRow.OrderedQty})`} sx={{ width: { xs: 180, sm: 240 } }} />
            )}

            <CustomButton disabled={scanningBlocked || (quantityRow != null && pendingWithoutPickingList)} title={quantityRow ? "Apply quantity" : "Submit"} size="small" iconButton={true} variant="outlined" icon={<ArrowForwardIcon />} onClick={handleScannerSubmit} />
            {quantityRow && <CustomButton title="Cancel" size="small" variant="outlined" onClick={closeQuantityEntry} />}

            <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" display="block" noWrap>Warehouse ID: {warehouseId ?? '-'}</Typography>
                <Typography variant="caption" display="block" noWrap sx={{ color: readyToDispatch ? 'success.main' : 'inherit', fontWeight: readyToDispatch ? 600 : 'inherit' }}>Status: {dispatchOrderStatus}</Typography>
            </Box>
        </Box>
    );
};

const CancelDispatchOrderVerification = ({ pickingListId, warehouseId }) => {
    useCancelDispatchOrderByPickingList(pickingListId, warehouseId);
    return null;
};

const DispatchOrderLookup = ({ lookupId, warehouseId, onResolved }) => {
    const { rows: orderResponse }: any = useGetDispatchOrderByWarehouse(lookupId, warehouseId);
    const { rows: pickingListResponse }: any = useGetDispatchOrderByPickingList(lookupId, warehouseId);
    const hasResolved = useRef(false);

    useEffect(() => {
        if (hasResolved.current) return;

        const order = Array.isArray(orderResponse) ? orderResponse[0] : orderResponse;
        const pickingListOrder = Array.isArray(pickingListResponse) ? pickingListResponse[0] : pickingListResponse;
        // Let the page display a mismatch instead of silently discarding the order.
        const matchesOrder = order?.DispatchOrderId != null &&
            String(order.DispatchOrderId) === String(lookupId);
        const matchesPickingList = pickingListOrder?.DispatchOrderId != null;

        const dispatchOrder = matchesOrder ? order : matchesPickingList ? pickingListOrder : null;

        // An empty response from one request must not discard the other lookup.
        if (!dispatchOrder) return;

        hasResolved.current = true;
        onResolved(dispatchOrder);
    }, [lookupId, warehouseId, orderResponse, pickingListResponse, onResolved]);

    return null;
};

// Use only the picking-list lookup here: looking up by order ID does not start verification.
const StartDispatchOrderVerification = ({ pickingListId, warehouseId, dispatchOrderId, onResolved }) => {
    const { rows: response }: any = useGetDispatchOrderByPickingList(pickingListId, warehouseId);
    const hasResolved = useRef(false);
    const onResolvedRef = useRef(onResolved);
    onResolvedRef.current = onResolved;

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            if (hasResolved.current) return;
            hasResolved.current = true;
            onResolvedRef.current(false);
        }, 15000);
        return () => window.clearTimeout(timeoutId);
    }, []);

    useEffect(() => {
        if (hasResolved.current) return;
        const order = Array.isArray(response) ? response[0] : response;
        if (!order?.DispatchOrderId) return;
        hasResolved.current = true;
        onResolvedRef.current(String(order.DispatchOrderId) === String(dispatchOrderId) && Number(order.OrderStatus?.Key ?? order.OrderStatus) === 5);
    }, [response, dispatchOrderId]);

    return null;
};

type ShipmentLabelJob = { key: number; rowId: string; shipmentId: number | string; packageId: number | string | null; packageCount: number; warehouseId: number | string; orderId: number | string; settings: DispatchPrintingSettings };
type ShipmentLabelPrintResult = { outcome?: PrintOutcome; error?: string };
type ShipmentLabelResponse = { ShipmentId: number | string; ShipmentPackageId?: number | string | null; LabelPdf?: string | null; ReturnLabelPdf?: string | null };

const getShipmentAddressLabel = (response: ShipmentLabelResponse | ShipmentLabelResponse[], job: ShipmentLabelJob): string => {
    const labels = (Array.isArray(response) ? response : [response]).filter((label) => label && String(label.ShipmentId) === String(job.shipmentId));
    const matches = job.packageId != null ? labels.filter((label) => String(label.ShipmentPackageId) === String(job.packageId)) : job.packageCount === 1 ? labels : [];
    if (matches.length !== 1) throw new Error('Could not identify a unique address label for this package. Reload the order and try again.');
    if (typeof matches[0].LabelPdf !== 'string' || !matches[0].LabelPdf.trim()) throw new Error('No address label was returned for this package.');
    return matches[0].LabelPdf;
};

// Mounted outside the grid so scrolling or changing tabs cannot restart a request.
const ShipmentLabelReprintJob = ({ job, onComplete }: { job: ShipmentLabelJob; onComplete: (result: ShipmentLabelPrintResult) => void }) => {
    const { rows: response, loading, status }: any = useGetShipmentLabelsByWarehouse(job.shipmentId, job.warehouseId);
    const claimed = useRef(false);
    const mounted = useRef(false);
    const completeRef = useRef(onComplete);
    completeRef.current = onComplete;
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            if (claimed.current || !mounted.current) return;
            claimed.current = true;
            completeRef.current({ error: 'Loading the address label timed out. Try reprinting it again.' });
        }, 30000);
        return () => window.clearTimeout(timeoutId);
    }, []);

    useEffect(() => {
        if (claimed.current || loading) return;
        const failed = status === 'error' || status === 'aborted' || status === 204 || (typeof status === 'number' && status >= 400);
        const data = response?.data ?? response;
        const hasResponse = Array.isArray(data) ? data.length > 0 : data?.ShipmentId != null;
        const succeeded = status === 'success' || (typeof status === 'number' && status >= 200 && status < 300);
        if (!hasResponse && !failed && !succeeded) return;
        claimed.current = true;
        if (failed || data == null) {
            completeRef.current({ error: `Could not load the address label for shipment ${job.shipmentId} (${status}).` });
            return;
        }
        let labelPdf: string;
        try { labelPdf = getShipmentAddressLabel(data, job); }
        catch (error) {
            completeRef.current({ error: error instanceof Error ? error.message : 'Could not identify the package address label.' });
            return;
        }
        const filename = `Shipment ${job.shipmentId}${job.packageId != null ? ` - Package ${job.packageId}` : ''} - Address label`;
        printDispatchPdf(labelPdf, job.settings, 'deliveryNote', filename)
            .then((outcome) => { if (mounted.current) completeRef.current({ outcome }); })
            .catch((error) => { if (mounted.current) completeRef.current({ error: error instanceof Error ? error.message : String(error) }); });
    }, [response, loading, status, job]);

    return null;
};

// UpdateShipment: save tracking first, then trigger customer sync without a body.
type UpdateShipmentJob = { key: number; scope: string; warehouseId: number | string; rows: any[] };

const UpdateShipment = ({ job, onComplete }: { job: UpdateShipmentJob; onComplete: (job: UpdateShipmentJob, rows: any[], error?: string) => void }) => {
    const shipmentId = job.rows[0].ShipmentId;
    const rows = useMemo(() => job.rows.filter((row) => String(row.ShipmentId) === String(shipmentId)), [job.rows, shipmentId]);
    const data = useMemo(() => ({
        Comment: 'Added New Tracking via Webclient',
        ShipmentPackages: rows.map((row) => ({ Id: row.ShipmentPackages_Id, ShipmentId: row.ShipmentId, TrackingNumber: row.ShipmentPackages_TrackingNumber, ReturnLabelTrackingNumber: row.ShipmentPackages_ReturnLabelTrackingNumber })),
    }), [rows]);
    const [step, setStep] = useState<'update' | 'sync'>('update');
    const update = useUpdateShipmentDispatchOrder(shipmentId, job.warehouseId, data);
    const sync = useTriggerSyncShipmentDispatchOrder(step === 'sync' ? shipmentId : null, job.warehouseId);
    const completed = useRef(false);

    useEffect(() => {
        const response = step === 'update' ? update : sync;
        if (completed.current || response.loading) return;
        // rows can contain cached shipment data; only the request status confirms success.
        const status = response.status;
        const failed = status === 'error' || status === 'aborted' || Number(status) >= 400;
        const succeeded = status === 'success' || (Number(status) >= 200 && Number(status) < 300);
        if (!failed && !succeeded) return;
        if (!failed && step === 'update') { setStep('sync'); return; }
        completed.current = true;
        onComplete(job, rows, failed ? (step === 'update' ? `Could not save tracking for shipment ${shipmentId}. Your edits are retained; try again.` : `Tracking was saved for shipment ${shipmentId}, but customer sync failed. Click Sync Tracking to Customer to retry.`) : undefined);
    }, [step, update.status, update.loading, sync.status, sync.loading, job, rows, shipmentId, onComplete]);

    return null;
};
